import { useState } from 'react';
import { UserAvatarStack } from '@cmp-src/components/UserAvatar/UserAvatarStack';
import { ResourceLocation } from '@cmp-src/data-display/resource/ResourceLocation';
import { AgendaSchedule, ISchedule, parseDateOnlyString } from '@gettactic/api';
import {
  findCheckinTargetSlot,
  getTimeDateObjectFromString
} from '@gettactic/helpers';
import { createMapLink } from '@gettactic/helpers';

import {
  ISlot,
  getReservationDataFromSlot
} from '@gettactic/helpers/src/reservation/getReservationDataFromSlot';
import { format } from 'date-fns-tz';
import { useMemo } from 'react';
import {
  AgendaCardButton,
  AgendaCardLayout,
  AgendaCardLayoutProps,
  DotsMenu
} from './AgendaCardLayout';

import { useAuthenticated } from '@gettactic/hooks';
import { IResource } from '@gettactic/api';
import { ViewDeskIcon } from '@cmp-src/icons/TacticIcons/VIewDeskIcon';
import { Button } from '@cmp-src/buttons';
import { useRouter } from 'next/router';
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@v2/components/ui/tooltip';
import { ConfirmModal } from '@cmp-src/overlay/modals/ConfirmModal';

export type ExtendedSchedule = Omit<ISchedule, 'resource'> & {
  resource: IResource & {
    location: string;
    floor: {
      id: string;
      name: string;
    };
    area: string;
  };
};

const getFormattedTime = (time: string) => {
  const date = getTimeDateObjectFromString(time);
  return format(date, 'HH:mm');
};

export interface CheckInCardProps {
  reservation: AgendaSchedule;
  layoutProps?: AgendaCardLayoutProps;
  onClick?: () => void;
  onCheckin?: (resourceId: string, scheduleId: string, slotId: string) => void;
  onCheckout?: (resourceId: string, scheduleId: string, slotId: string) => void;
  loading?: boolean;
  showCheckInButton?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
}

export const CheckInCard = ({
  reservation,
  layoutProps,
  onClick,
  onCheckin,
  onCheckout,
  loading = false,
  showCheckInButton: showCheckInButtonProp,
  onEdit,
  onCancel
}: CheckInCardProps) => {
  const [isCheckOutModalOpen, setIsCheckOutModalOpen] = useState(false);
  const areas = reservation.resource.areas || [];

  const router = useRouter();

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const officeId = reservation.resource.office_id;
  const parentId = reservation.resource.parent_id;
  const officeName =
    authenticatedUser.offices?.byId[
      officeId as keyof typeof authenticatedUser.offices.byId
    ]?.name;
  const areaName = parentId
    ? areas.find((area) => area.id === parentId)?.name
    : undefined;

  const start = useMemo(() => {
    return format(
      parseDateOnlyString(reservation.start.replace('Z', '')),
      'h:mm a'
    );
  }, [reservation.start]);

  const end = useMemo(() => {
    return format(
      parseDateOnlyString(reservation.end.replace('Z', '')),
      'h:mm a'
    );
  }, [reservation.end]);

  const targetSlot = findCheckinTargetSlot(
    reservation,
    authenticatedUser.user?.id ?? ''
  );

  const { allowCheckIn, allowCheckOut, canEdit, canRespondCancel } =
    getReservationDataFromSlot(targetSlot as unknown as ISlot);

  const handleCheckInClick = () => {
    if (!onCheckin) return;
    const slot = reservation.slots[0];
    onCheckin(slot.resource.id, reservation.id, slot.id);
  };

  const handleCheckOutClick = () => {
    if (!onCheckout) return;
    const slot = reservation.slots[0];
    onCheckout(slot.resource.id, reservation.id, slot.id);
    setIsCheckOutModalOpen(false);
  };

  const _showCheckInButton =
    showCheckInButtonProp !== undefined
      ? showCheckInButtonProp
      : !reservation.has_checked_in;

  const floor = reservation.resource.floor;

  const link = createMapLink({
    date: reservation.start,
    officeId: reservation.resource.office_id,
    resourceId: reservation.resource?.id,
    floorId: reservation.resource?.parent_id ?? undefined,
    startTime: reservation.start,
    endTime: reservation.end,
  });

  const formattedCheckOutTime = targetSlot?.check_out_time
    ? format(new Date(targetSlot?.check_out_time), 'HH:mm')
    : null;

  const isMeetingRoom = reservation.resource.type === 'meeting_room';

  return (
    <AgendaCardLayout
      onCardClick={onClick}
      topLeft={
        <div className="flex items-center">
          {start} - {end}{' '}
          {targetSlot?.check_out_time ? (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <span className="ml-1 text-neutral-500 text-sm">
                    ({formattedCheckOutTime})
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Checked out at {formattedCheckOutTime}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </div>
      }
      dotsMenu={
        (canEdit || canRespondCancel) && (
          <DotsMenu
            onManage={onEdit}
            onRemove={onCancel}
            canManage={canEdit}
            canRemove={canRespondCancel}
            onlineMeeting={reservation.online_meeting}
          />
        )
      }
      bottomLeft={
        <div className="flex flex-col space-y-1">
          <Button
            variant="custom"
            className="group/resource justify-left !px-0 !py-0 flex flex-row items-start font-neutral-900"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              router.push(link);
            }}
          >
            <div className="max-w-[250px] truncate text-left text-neutral-1000">
              {reservation.resource.name}{' '}
            </div>
            <div className="!px-0 !py-0 ml-1 hidden items-center font-bold group-hover/resource:block">
              <ViewDeskIcon
                className=" h-4 w-4 "
                pathProps={{
                  fill: 'hsl(var(--primary))'
                }}
              />
            </div>
          </Button>
          <div className="flex max-w-[250px] items-center">
            <ResourceLocation
              location={officeName}
              floor={floor.name}
              area={areaName}
              className="truncate text-neutral-500"
            />
          </div>
        </div>
      }
      bottomRight={
        isMeetingRoom ? (
          <div className="flex items-end">
            <UserAvatarStack
              users={reservation.slots.map((slot) => slot.user)}
            />
          </div>
        ) : null
      }
      button={
        (allowCheckIn && _showCheckInButton) ||
          allowCheckOut ? (
          <>
            {allowCheckIn && _showCheckInButton && (
              <AgendaCardButton onClick={handleCheckInClick} disabled={loading}>
                {loading ? 'Processing...' : 'Check in'}
              </AgendaCardButton>
            )}
            {allowCheckOut && (
              <AgendaCardButton
                onClick={() => setIsCheckOutModalOpen(true)}
                disabled={loading}
                variant="secondary"
              >
                {loading ? 'Processing...' : 'Check out'}
              </AgendaCardButton>
            )}
            {isCheckOutModalOpen && (
              <ConfirmModal
                title="Check out"
                message="Are you sure you want to check out?"
                onConfirm={handleCheckOutClick}
                onOpenChange={setIsCheckOutModalOpen}
                onCancel={() => setIsCheckOutModalOpen(false)}
                isOpen={isCheckOutModalOpen}
              />
            )}
          </>
        ) : null
      }
      hasHover={!!reservation}
      {...layoutProps}
    />
  );
};
